import { ChangeDetectionStrategy, Component, effect, inject, signal, untracked } from '@angular/core';
import { LiveSessionIconSvgComponent } from '../svg-sprites/general/live-session-icon-svg.component';
import { LessonsDBService } from '../services/lessons-db.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '../store';
import { isAdmin, isCourseAdmin, isLoggedOut, selectUserCourses } from '../store/selectors';
import { UserPermissionsService } from '../shared/services/user-permissions.service';
import { PermissionKeys } from '../shared/services/permissions-key.enum';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'live-session-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LiveSessionIconSvgComponent],
  templateUrl: './live-session-bar.component.html',
  styleUrl: './live-session-bar.component.scss'
})
export class LiveSessionBarComponent {
  private lessonsDB = inject(LessonsDBService);
  private router = inject(Router);
  private store = inject<Store<AppState>>(Store);
  private userPermissions = inject(UserPermissionsService);

  private readonly sessions = toSignal(this.lessonsDB.loadAllVisibleLiveSessionsAndWebinars());
  private readonly loggedOut = toSignal(this.store.select(isLoggedOut));
  private readonly admin = toSignal(this.store.select(isAdmin));
  private readonly courseAdmin = toSignal(this.store.select(isCourseAdmin));
  private readonly userCourses = toSignal(this.store.select(selectUserCourses));

  readonly isBannerVisible = signal(false);
  readonly currentLiveSession = signal<any>(null);
  private readonly dismissedSessionKey = signal<string | null>(null);

  constructor() {
    effect(() => {
      const sessions = this.sessions();
      const isLoggedOutVal = this.loggedOut();
      const isAdminVal = this.admin();
      const isCourseAdminVal = this.courseAdmin();
      const userCoursesVal = this.userCourses();

      if (!sessions || isLoggedOutVal) {
        this.isBannerVisible.set(false);
        this.currentLiveSession.set(null);
        return;
      }

      const now = new Date();
      const liveSessions = sessions
        .filter(session => {
          const s = session.liveSession || session.webinar;
          if (!s?.sessionDate) return false;
          const start = s.sessionDate.toDate();
          const durationHours = s.sessionDuration?.hours || 1;
          const durationMinutes = s.sessionDuration?.minutes || 0;
          const end = new Date(start);
          end.setHours(end.getHours() + durationHours);
          end.setMinutes(end.getMinutes() + durationMinutes);
          return now >= start && now <= end;
        })
        .sort((a, b) => {
          const aStart = (a.liveSession || a.webinar).sessionDate.toDate().getTime();
          const bStart = (b.liveSession || b.webinar).sessionDate.toDate().getTime();
          return aStart - bStart;
        });

      if (!liveSessions.length) {
        this.isBannerVisible.set(false);
        this.currentLiveSession.set(null);
        return;
      }

      const isStudent = !isAdminVal && !isCourseAdminVal &&
        !this.userPermissions.can(PermissionKeys.EditProduct);

      let targetSession: any = null;
      if (!isStudent) {
        targetSession = liveSessions[0];
      } else {
        const enrolledIds = new Set((userCoursesVal || []).map(c => c.id));
        targetSession = liveSessions.find(s => enrolledIds.has(s.courseId)) ?? null;
      }

      if (!targetSession) {
        this.isBannerVisible.set(false);
        this.currentLiveSession.set(null);
        return;
      }

      this.currentLiveSession.set(targetSession);
      const sessionKey = `${targetSession.courseId}-${targetSession.seqNo}`;
      if (sessionKey !== untracked(() => this.dismissedSessionKey())) {
        this.isBannerVisible.set(true);
      }
    }, { allowSignalWrites: true });
  }

  joinLiveSession() {
    const session = this.currentLiveSession();
    if (session) {
      this.router.navigate([
        '/courses',
        session.courseUrl,
        session.sectionSeqNo || 1,
        'lessons',
        session.seqNo
      ]);
    }
  }

  closeLiveSolutionBanner() {
    const session = this.currentLiveSession();
    this.dismissedSessionKey.set(session ? `${session.courseId}-${session.seqNo}` : null);
    this.isBannerVisible.set(false);
  }
}
